// src/components/widgets/MetasWidget.js
import React, { useState, useEffect } from 'react';
import { db, auth } from '../../services/firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { BigQueryService } from '../../services/bigQueryService';
import { useCatalogos } from '../../context/CatalogosContext';
import { TargetIcon, ClockIcon, ArrowPathIcon, ExclamationTriangleIcon, SparklesIcon, TrophyIcon, CheckCircleIcon, ArrowTrendingUpIcon } from '../ui/Icons';
import useEsMovil from '../../hooks/useEsMovil';
import useConteoAnimado from '../../hooks/useConteoAnimado';
import { ESTILO_TILE, BadgeTendencia } from './widgetsUI';

// Estilos responsivos para móvil
const estilosResponsivos = `
  @media (max-width: 768px) {
    .metas-widget-container {
      margin: 15px 0 !important;
    }
    
    .metas-header {
      flex-direction: column !important;
      align-items: center !important;
      text-align: center;
      margin-bottom: 12px !important;
    }

    .metas-header-der {
      width: 100%;
      flex-direction: column !important;
      gap: 8px !important;
    }

    .metas-tiles {
      grid-template-columns: 1fr 1fr !important;
      gap: 8px !important;
    }

    .metas-tile {
      padding: 10px !important;
    }

    .viz-card-metas {
      padding: 12px !important;
    }
    
    .metas-header h3 {
      font-size: 18px !important;
      margin: 0 !important;
    }
    
    .metas-header small {
      font-size: 11px !important;
    }
    
    .btn-actualizar-metas {
      width: 100% !important;
      padding: 10px 16px !important;
      font-size: 13px !important;
    }
    
    .alerta-sin-meta {
      padding: 12px !important;
      margin-bottom: 12px !important;
    }
    
    .alerta-sin-meta-texto {
      font-size: 13px !important;
      margin-bottom: 8px !important;
    }
    
    .alerta-sin-meta-info {
      font-size: 12px !important;
      flex-direction: column !important;
      gap: 5px !important;
    }
    
    .progreso-info {
      font-size: 13px !important;
      margin-bottom: 8px !important;
    }

    .progreso-barra {
      height: 25px !important;
    }

    .metas-viz-grid {
      grid-template-columns: 1fr !important;
      gap: 10px !important;
    }

    .metas-felicitacion {
      font-size: 13px !important;
      margin: 8px 0 0 0 !important;
    }
  }
`;

function MetasWidget() {
  // Usuarios desde Context (sin queries en mount)
  const { usuarios: usuariosCtx } = useCatalogos();

  const [metaData, setMetaData] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Esperar a que el Context tenga los usuarios cargados antes de buscar
    if (usuariosCtx.length > 0) {
      cargarMetasUsuario(false);
    }
  }, [usuariosCtx]);

  const cargarMetasUsuario = async (forzar = false) => {
    try {
      setLoading(true);
      const email = auth.currentUser?.email;
      if (!email) {
        setLoading(false);
        return;
      }

      // Buscar el usuario actual en el Context (sin query a Firestore)
      const userData = usuariosCtx.find(u => u.email === email);
      const nombreUsuario = userData?.nombre || '';

      const año = new Date().getFullYear().toString();
      const mes = (new Date().getMonth() + 1).toString().padStart(2, '0');

      // Solo por el correo de quien entró: cada colaborador puede leer únicamente SU meta (regla de
      // `metas_sac`), y una consulta por nombre se rechazaría entera. Medido el 06/10/2026: las 60 metas
      // guardadas tienen el correo exacto de su cuenta, así que buscar por nombre no encontraba nada más.
      const metasSnapshot = await getDocs(
        query(
          collection(db, 'metas_sac'),
          where('usuarioEmail', '==', email),
          where('año', '==', año),
          where('mes', '==', mes)
        )
      );

      let meta = null;
      if (!metasSnapshot.empty) {
        meta = metasSnapshot.docs[0].data();
      }

      // Lectura DIRECTA del doc precalculado en Firestore (sin Cloud Function
      // → sin cold start). Forzar o doc inexistente → CF, que recalcula con
      // BigQuery y refresca el doc.
      let datosMetas = null;
      if (!forzar) {
        const snapMetas = await getDoc(doc(db, 'estadisticas_metas_usuario', `${email}_${año}_${mes}`));
        if (snapMetas.exists()) {
          const dataMetas = snapMetas.data();
          datosMetas = { ...dataMetas.datos, ultimaActualizacion: dataMetas.ultimaActualizacion, fromCache: true };
        }
      }
      if (!datosMetas) {
        datosMetas = await BigQueryService.obtenerDatosMetasUsuario(
          año,
          mes,
          email,
          nombreUsuario, // Pasar el nombre para filtrar por email O por nombre
          forzar
        );
      }

      const pedidosValidos = datosMetas.pedidosValidos || 0;
      const promedioReal = datosMetas.proyecciones?.promedioDiario || 0;

      let bonoUnitario = 0;
      let rangoActual = 0;
      let montoTotal = 0;
      let rangos = [];
      let metaMinima = 0;
      
      if (meta && meta.rangos) {
        rangos = meta.rangos;
        metaMinima = meta.rangos[0]?.minimo || 0;
        
        for (const rango of meta.rangos) {
          if (rango.numero === 4) {
            if (pedidosValidos >= rango.minimo) {
              bonoUnitario = rango.bono;
              montoTotal = pedidosValidos * rango.bono;
              rangoActual = 4;
            }
          } else {
            if (pedidosValidos >= rango.minimo && pedidosValidos <= rango.maximo) {
              bonoUnitario = rango.bono;
              montoTotal = pedidosValidos * rango.bono;
              rangoActual = rango.numero;
              break;
            }
          }
        }
      }

      const porcentaje = metaMinima > 0 ? (pedidosValidos / metaMinima) * 100 : 0;

      const hoy = new Date();
      const diaActual = hoy.getDate();
      const ultimoDia = new Date(parseInt(año), parseInt(mes), 0).getDate();
      
      let diasHabilesTranscurridos = 0;
      for (let dia = 1; dia <= diaActual; dia++) {
        const fecha = new Date(hoy.getFullYear(), hoy.getMonth(), dia);
        const diaSemana = fecha.getDay();
        if (diaSemana !== 0 && diaSemana !== 6) {
          diasHabilesTranscurridos++;
        }
      }
      
      let diasHabilesRestantes = 0;
      for (let dia = diaActual + 1; dia <= ultimoDia; dia++) {
        const fecha = new Date(hoy.getFullYear(), hoy.getMonth(), dia);
        const diaSemana = fecha.getDay();
        if (diaSemana !== 0 && diaSemana !== 6) {
          diasHabilesRestantes++;
        }
      }
      
      const faltanParaMeta = Math.max(0, metaMinima - pedidosValidos);
      
      setMetaData({
        meta: metaMinima,
        pedidos: pedidosValidos,
        porcentaje: Math.min(100, porcentaje),
        faltan: faltanParaMeta,
        rangos: rangos,
        rangoActual,
        bonoUnitario,
        montoTotal,
        ultimaActualizacion: datosMetas.ultimaActualizacion,
        fromCache: datosMetas.fromCache,
        promedios: {
          diasHabilesTranscurridos,
          diasHabilesRestantes,
          promedioDiarioActual: promedioReal,
          pedidosDiariosNecesarios: diasHabilesRestantes > 0 && faltanParaMeta > 0 ? 
            Math.ceil(faltanParaMeta / diasHabilesRestantes) : null
        }
      });

    } catch (error) {
      console.error('Error cargando metas:', error);
    }
    setLoading(false);
  };

  const formatearFechaHora = (timestamp) => {
    if (!timestamp) return 'No disponible';
    
    try {
      let fecha;
      
      if (timestamp._seconds) {
        fecha = new Date(timestamp._seconds * 1000);
      } else if (timestamp.seconds) {
        fecha = new Date(timestamp.seconds * 1000);
      } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        fecha = timestamp.toDate();
      } else {
        fecha = new Date(timestamp);
      }
      
      if (isNaN(fecha.getTime())) {
        return 'No disponible';
      }
      
      const opciones = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/Guatemala'
      };
      
      return fecha.toLocaleTimeString('es-GT', opciones);
    } catch (error) {
      return 'No disponible';
    }
  };

  const isMobile = useEsMovil();

  // Números con conteo animado + barra de progreso que se llena al aparecer
  const pedidosAnimado = useConteoAnimado(metaData?.pedidos || 0);
  const bonoAnimado = useConteoAnimado(metaData?.montoTotal || 0);
  const [barraLista, setBarraLista] = useState(false);
  useEffect(() => {
    if (!metaData) return;
    setBarraLista(false);
    const t = setTimeout(() => setBarraLista(true), 60);
    return () => clearTimeout(t);
  }, [metaData]);

  if (loading) {
    // Sin el Context de usuarios todavía no se sabe si el widget aplica
    if (usuariosCtx.length === 0) return null;
    // Skeleton con la silueta del widget
    return (
      <>
        <style>{estilosResponsivos}</style>
        <div className="metas-widget-container" style={{ maxWidth: '1000px', margin: isMobile ? '15px 0' : '30px auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: isMobile ? '12px' : '16px' }}>
            <div className="skeleton" style={{ width: '210px', height: '24px', borderRadius: 'var(--radio-sm)' }} />
            <div className="skeleton" style={{ width: '280px', height: '34px', borderRadius: 'var(--radio-sm)' }} />
          </div>
          <div className="skeleton" style={{ width: '100%', height: '86px', borderRadius: 'var(--radio-lg)', marginBottom: '14px' }} />
          <div className="metas-tiles" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))', gap: isMobile ? '8px' : '14px', marginBottom: '14px' }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="skeleton" style={{ height: '84px', borderRadius: 'var(--radio-lg)' }} />
            ))}
          </div>
          <div className="metas-viz-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: isMobile ? '10px' : '14px' }}>
            {[0, 1].map((i) => (
              <div key={i} className="skeleton" style={{ height: '160px', borderRadius: 'var(--radio-lg)' }} />
            ))}
          </div>
        </div>
      </>
    );
  }
  if (!metaData) return null;

  // === Lectura por RITMO (no por % absoluto): a mitad de mes llevar 40% es
  // normal — lo que importa es si el promedio diario alcanza para llegar. ===
  const promedioActual = metaData.promedios.promedioDiarioActual || 0;
  const necesarios = metaData.promedios.pedidosDiariosNecesarios; // null si ya no faltan
  const diasTrans = metaData.promedios.diasHabilesTranscurridos || 0;
  const diasRest = metaData.promedios.diasHabilesRestantes || 0;
  const totalDiasHabiles = diasTrans + diasRest;
  // Dónde "debería" ir hoy la barra si avanzara parejo por días hábiles
  const pctEsperadoHoy = metaData.meta > 0 && totalDiasHabiles > 0
    ? Math.min(100, (diasTrans / totalDiasHabiles) * 100)
    : 0;
  const metaLograda = metaData.meta > 0 && metaData.faltan === 0;
  // Sin días hábiles restantes y todavía falta → ya no alcanza (no pintar verde)
  const ritmoOk = metaLograda || (necesarios ? promedioActual >= necesarios : diasRest > 0);
  const ritmoCasi = !ritmoOk && necesarios && promedioActual >= necesarios * 0.85;
  const colorRitmo = ritmoOk ? 'var(--color-exito)' : ritmoCasi ? 'var(--color-advertencia)' : 'var(--color-peligro)';
  const colorRitmoTexto = ritmoOk ? 'var(--color-exito-texto)' : ritmoCasi ? 'var(--color-advertencia-texto)' : 'var(--color-peligro-texto)';
  const pctVsNecesario = necesarios > 0 ? ((promedioActual - necesarios) / necesarios) * 100 : null;
  // A este ritmo, cuántos pedidos cerraría el mes
  const proyeccionCierre = Math.round(metaData.pedidos + promedioActual * diasRest);
  // Próximo rango (para la mini barra del tile y la escalera)
  const rangoSiguiente = (metaData.rangos || []).find(r => r.numero === metaData.rangoActual + 1) || null;
  const baseRango = metaData.rangoActual > 0
    ? ((metaData.rangos || []).find(r => r.numero === metaData.rangoActual)?.minimo || 0)
    : 0;
  const pctHaciaSiguiente = rangoSiguiente && rangoSiguiente.minimo > baseRango
    ? Math.min(100, Math.max(0, ((metaData.pedidos - baseRango) / (rangoSiguiente.minimo - baseRango)) * 100))
    : 100;

  return (
    <>
      <style>{estilosResponsivos}</style>
      <div className="metas-widget-container" style={{
        maxWidth: '1000px',
        margin: isMobile ? '15px 0' : '30px auto'
      }}>
        {/* Header estilo dashboard: título a la izquierda, metadata + botón a la
            derecha (en móvil el CSS lo apila y centra) */}
        <div className="metas-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px',
          marginBottom: isMobile ? '12px' : '16px'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: isMobile ? '18px' : '22px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ color: 'var(--color-primario-fuerte)', display: 'inline-flex' }}><TargetIcon className="w-6 h-6" /></span>
            Mi Meta del Mes
          </h3>
          <div className="metas-header-der" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {metaData.ultimaActualizacion && (
              <small
                title={metaData.fromCache ? 'Datos precalculados (se actualizan solos cada hora)' : 'Recalculado al momento'}
                style={{ color: 'var(--color-texto-secundario)', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
              >
                <span style={{ display: 'inline-flex' }}><ClockIcon className="w-3 h-3" /></span>
                Actualizado {formatearFechaHora(metaData.ultimaActualizacion)} · cada hora
              </small>
            )}
            <button
              onClick={() => cargarMetasUsuario(true)}
              className="btn-actualizar-metas btn-press hover-bright"
              style={{
                height: '38px',
                boxSizing: 'border-box',
                padding: '0 20px',
                backgroundColor: 'var(--color-primario)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radio-sm)',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
              title="Forzar recálculo de metas"
            >
              <ArrowPathIcon className="w-4 h-4" /> Actualizar
            </button>
          </div>
        </div>
        
        {/* Progreso de la meta */}
        {metaData.meta === 0 ? (
          <div className="alerta-sin-meta" style={{
            padding: isMobile ? '12px' : '15px',
            backgroundColor: 'var(--color-advertencia-fondo)',
            border: '1px solid var(--color-advertencia)',
            borderRadius: 'var(--radio-lg)',
            marginBottom: isMobile ? '10px' : '14px'
          }}>
            <div className="alerta-sin-meta-texto" style={{
              textAlign: 'center',
              marginBottom: isMobile ? '8px' : '10px',
              color: 'var(--color-advertencia-texto)',
              fontSize: isMobile ? '13px' : '14px'
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ color: 'var(--color-advertencia-texto)', display: 'inline-flex' }}><ExclamationTriangleIcon className="w-4 h-4" /></span>
                Sin meta establecida
              </span>
            </div>
            <div className="alerta-sin-meta-info" style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: isMobile ? '12px' : '13px',
              fontWeight: 'bold',
              color: 'var(--color-advertencia-texto)',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '5px' : '0'
            }}>
              <span>Pedidos realizados: {metaData.pedidos}</span>
              <span>Promedio: {Math.round(metaData.promedios.promedioDiarioActual)} pedidos/día</span>
            </div>
          </div>
        ) : (
          <div className="card viz-card-metas" style={{ ...ESTILO_TILE, marginBottom: isMobile ? '10px' : '14px' }}>
            <div className="progreso-info" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              flexWrap: 'wrap',
              gap: '6px',
              marginBottom: isMobile ? '8px' : '10px',
              fontSize: isMobile ? '13px' : '14px'
            }}>
              <span>Pedidos: <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round(pedidosAnimado)}</strong></span>
              <span style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', fontVariantNumeric: 'tabular-nums' }}>
                <strong style={{ color: 'var(--color-texto)' }}>{metaData.porcentaje.toFixed(1)}%</strong>
                {metaData.faltan > 0 && ` · faltan ${metaData.faltan}`}
              </span>
              <span>Meta: <strong>{metaData.meta}</strong></span>
            </div>

            {/* Barra: el COLOR es por ritmo (¿tu promedio alcanza para llegar?),
                y el marcador "hoy" muestra dónde iría un avance parejo */}
            <div className="progreso-barra" style={{
              width: '100%',
              height: isMobile ? '25px' : '30px',
              backgroundColor: 'var(--color-borde)',
              borderRadius: 'var(--radio-sm)',
              overflow: 'hidden',
              position: 'relative'
            }}>
              <div style={{
                width: barraLista ? `${metaData.porcentaje}%` : '0%',
                height: '100%',
                backgroundColor: colorRitmo,
                transition: 'width 0.8s ease'
              }} />
              {pctEsperadoHoy > 0 && pctEsperadoHoy < 100 && !metaLograda && (
                <div
                  title={`Avance parejo esperado a hoy: ${pctEsperadoHoy.toFixed(0)}%`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${pctEsperadoHoy}%`,
                    width: '2px',
                    backgroundColor: 'var(--color-texto)',
                    opacity: 0.55
                  }}
                />
              )}
            </div>

            {/* Frase de estado por ritmo + referencia del marcador */}
            {!metaLograda && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '6px',
                marginTop: '8px',
                fontSize: isMobile ? '11px' : '12px'
              }}>
                <span style={{ color: colorRitmoTexto, fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ display: 'inline-flex', transform: ritmoOk ? 'none' : 'scaleY(-1)' }}>
                    <ArrowTrendingUpIcon className="w-4 h-4" />
                  </span>
                  {ritmoOk
                    ? 'Vas arriba del ritmo — a este paso la meta llega'
                    : ritmoCasi
                      ? 'Vas casi al ritmo — un empujón más y llegás'
                      : 'Vas abajo del ritmo — se necesita acelerar'}
                </span>
                {pctEsperadoHoy > 0 && (
                  <span style={{ color: 'var(--color-texto-secundario)' }}>
                    avance parejo a hoy: {pctEsperadoHoy.toFixed(0)}%
                  </span>
                )}
              </div>
            )}

            {metaData.porcentaje >= 100 && (
              <p className="metas-felicitacion" style={{
                textAlign: 'center',
                color: 'var(--color-exito)',
                fontWeight: 'bold',
                fontSize: isMobile ? '13px' : '14px',
                margin: '10px 0 0 0'
              }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span className="check-pop" style={{ color: 'var(--color-exito)', display: 'inline-flex' }}><SparklesIcon className="w-4 h-4" /></span>
                  ¡Felicidades! Has alcanzado tu meta
                </span>
              </p>
            )}
          </div>
        )}

        {/* KPI tiles de la meta — mismo lenguaje del faro (Estadísticas del Día):
            número grande con color semántico + contexto que cuenta la historia */}
        <div className="metas-tiles stagger-list" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))',
          gap: isMobile ? '8px' : '14px'
        }}>
          {metaData.meta > 0 && (
            <div className="card metas-tile" style={ESTILO_TILE} title="Rango de bono según tus pedidos válidos del mes">
              <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Rango actual</div>
              <div style={{ fontSize: '22px', fontWeight: 'bold', color: metaData.rangoActual > 0 ? 'var(--color-exito)' : 'var(--color-texto)' }}>
                {metaData.rangoActual > 0 ? `Rango ${metaData.rangoActual}` : 'Sin rango aún'}
              </div>
              {rangoSiguiente ? (
                <>
                  <div style={{ height: '6px', backgroundColor: 'var(--color-borde)', borderRadius: 'var(--radio-sm)', overflow: 'hidden', marginTop: '8px' }}>
                    <div style={{
                      width: barraLista ? `${pctHaciaSiguiente}%` : '0%',
                      height: '100%',
                      backgroundColor: 'var(--color-primario)',
                      transition: 'width 0.8s ease'
                    }} />
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '4px', fontVariantNumeric: 'tabular-nums' }}>
                    faltan {Math.max(0, rangoSiguiente.minimo - metaData.pedidos).toLocaleString()} para Rango {rangoSiguiente.numero}
                  </div>
                </>
              ) : metaData.rangoActual > 0 && (
                <div style={{ fontSize: '11px', color: 'var(--color-exito-texto)', marginTop: '4px' }}>
                  rango máximo alcanzado
                </div>
              )}
            </div>
          )}

          <div className="card metas-tile" style={ESTILO_TILE} title="Bono acumulado con el rango en el que vas">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Bono alcanzado</div>
            <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-exito)', fontVariantNumeric: 'tabular-nums' }}>
              Q{bonoAnimado.toFixed(2)}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
              ({metaData.pedidos} × Q{metaData.bonoUnitario.toFixed(2)})
            </div>
          </div>

          <div className="card metas-tile" style={ESTILO_TILE} title="Tu promedio real de pedidos por día hábil, comparado contra el necesario">
            <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Tu ritmo</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '22px', fontWeight: 'bold', color: colorRitmoTexto, fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(promedioActual)}/día
              </span>
              <BadgeTendencia
                pct={pctVsNecesario}
                etiqueta="vs necesario"
                textoIgual="al ritmo justo"
                titulo="Tu promedio diario comparado contra el necesario para llegar a la meta"
              />
            </div>
            <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
              pedidos por día hábil
            </div>
          </div>

          {metaData.meta > 0 && (
            <div className="card metas-tile" style={ESTILO_TILE} title="Ritmo que se necesita en los días hábiles que quedan">
              <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Ritmo necesario</div>
              {metaData.faltan > 0 ? (
                <>
                  <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>
                    {necesarios ? `${necesarios}/día` : metaData.faltan}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
                    {necesarios
                      ? `en los ${diasRest} días hábiles que quedan`
                      : 'pedidos faltantes'}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-exito)' }}>¡Lograda!</div>
                  <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>meta del mes cumplida</div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Visualizaciones: escalera de rangos + proyección del mes */}
        {(metaData.rangos.length > 0 || metaData.meta > 0) && (
          <div className="metas-viz-grid stagger-list" style={{
            display: 'grid',
            gridTemplateColumns: isMobile || !(metaData.rangos.length > 0 && metaData.meta > 0) ? '1fr' : 'repeat(2, minmax(0, 1fr))',
            gap: isMobile ? '10px' : '14px',
            marginTop: isMobile ? '10px' : '14px'
          }}>
            {/* Escalera de rangos: siempre visible, el actual resaltado */}
            {metaData.rangos.length > 0 && (
              <div className="card viz-card-metas" style={{ ...ESTILO_TILE, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ display: 'inline-flex' }}><TrophyIcon className="w-4 h-4" /></span> Escalera de rangos
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, justifyContent: 'center' }}>
                  {metaData.rangos.map((rango) => {
                    const alcanzado = metaData.pedidos >= rango.minimo;
                    const esActual = metaData.rangoActual === rango.numero;
                    return (
                      <div key={rango.numero} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '5px 8px',
                        borderRadius: 'var(--radio-sm)',
                        backgroundColor: esActual ? 'var(--color-exito-fondo)' : 'transparent',
                        fontSize: '12px'
                      }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontWeight: esActual ? 'bold' : 'normal',
                          color: alcanzado ? 'var(--color-exito-texto)' : 'var(--color-texto-secundario)'
                        }}>
                          {alcanzado ? (
                            <CheckCircleIcon className="w-4 h-4" />
                          ) : (
                            <span style={{
                              width: '14px', height: '14px', flexShrink: 0,
                              border: '2px solid var(--color-borde)',
                              borderRadius: '50%', display: 'inline-block', boxSizing: 'border-box'
                            }} />
                          )}
                          Rango {rango.numero} · {rango.minimo.toLocaleString()}-{rango.numero === 4 ? '∞' : rango.maximo.toLocaleString()}
                          {esActual && <span style={{ fontSize: '10px', fontWeight: 'bold', color: 'var(--color-exito-texto)' }}>← vas aquí</span>}
                        </span>
                        <strong style={{ color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          Q{rango.bono.toFixed(2)}
                        </strong>
                      </div>
                    );
                  })}
                </div>
                {rangoSiguiente && (
                  <div style={{ borderTop: '1px solid var(--color-borde-suave)', marginTop: '8px', paddingTop: '8px', fontSize: '11px', color: 'var(--color-texto-secundario)', fontVariantNumeric: 'tabular-nums' }}>
                    Siguiente escalón: {Math.max(0, rangoSiguiente.minimo - metaData.pedidos).toLocaleString()} pedidos más → Q{rangoSiguiente.bono.toFixed(2)} c/u
                  </div>
                )}
              </div>
            )}

            {/* Proyección del mes: tu ritmo vs el necesario + cierre estimado */}
            {metaData.meta > 0 && (
              <div className="card viz-card-metas" style={{ ...ESTILO_TILE, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ display: 'inline-flex' }}><ArrowTrendingUpIcon className="w-4 h-4" /></span> Proyección del mes
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '10px' }}>
                  {[
                    { etiqueta: 'Tu ritmo', valor: promedioActual, color: colorRitmo, colorTexto: colorRitmoTexto },
                    ...(necesarios ? [{ etiqueta: 'Necesario', valor: necesarios, color: 'var(--color-neutro-solido)', colorTexto: 'var(--color-texto-secundario)' }] : [])
                  ].map((fila) => {
                    const maxRef = Math.max(promedioActual, necesarios || 0, 1);
                    return (
                      <div key={fila.etiqueta}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                          <span style={{ color: fila.colorTexto, fontWeight: 'bold' }}>{fila.etiqueta}</span>
                          <strong style={{ color: fila.colorTexto, fontVariantNumeric: 'tabular-nums' }}>{Math.round(fila.valor)}/día</strong>
                        </div>
                        <div style={{ height: '10px', backgroundColor: 'var(--color-borde)', borderRadius: 'var(--radio-sm)', overflow: 'hidden' }}>
                          <div style={{
                            width: barraLista ? `${(fila.valor / maxRef) * 100}%` : '0%',
                            height: '100%',
                            backgroundColor: fila.color,
                            transition: 'width 0.8s ease'
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ borderTop: '1px solid var(--color-borde-suave)', marginTop: '10px', paddingTop: '8px', fontSize: '12px' }}>
                  {metaLograda ? (
                    <span style={{ color: 'var(--color-exito-texto)', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                      <CheckCircleIcon className="w-4 h-4" /> ¡Meta cumplida! Cada pedido extra sigue sumando bono
                    </span>
                  ) : proyeccionCierre >= metaData.meta ? (
                    <span style={{ color: 'var(--color-exito-texto)', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                      <CheckCircleIcon className="w-4 h-4" /> A este ritmo cerrarías en ~{proyeccionCierre.toLocaleString()} pedidos, arriba de la meta
                    </span>
                  ) : (
                    <span style={{ color: colorRitmoTexto, fontWeight: 'bold' }}>
                      A este ritmo cerrarías en ~{proyeccionCierre.toLocaleString()} — a {(metaData.meta - proyeccionCierre).toLocaleString()} de la meta
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default MetasWidget;